import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import * as _ from 'lodash';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import {usePaypalRequestsContext} from '../context/PaypalRequestsContext';
import FilterTextField from '../../_common/components/FilterTextField';
import FilterSelect from '../../_common/components/FilterSelect';
import {FilterFromDate, FilterToDate} from '../../_common/components/FilterDateRange';
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import * as actions from '../_redux/paypalRequestsActions';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {generateExcelFile} from '../../_common/utils/excel';
import {paypalRequestsHeader} from '../../_common/constants/exportedFilesMainHeaders';
import {headersPaypalRequests} from '../../_common/constants/exportedFilesHeaders';
import {paypalPaymentStatusOptions} from '../../_common/constants/options';
import {getCurrencyValue} from '../utils/getCurrencyValue';
import FiltersBar from '../../_common/components/FiltersBar';

const prepareFilter = (queryParams, values) => {
  const {
    agentName,
    agentID,
    paymentStatus,
    fromDate,
    toDate,
    paypalPaymentID,
  } = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(paypalPaymentID)) {
    filter.paypalPaymentID = paypalPaymentID;
  } else {
    delete newQueryParams.paypalPaymentID;
  }
  if (Boolean(paymentStatus)) {
    filter.paymentStatus = paymentStatus;
  } else {
    delete newQueryParams.paymentStatus;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(agentName)) {
    filter.agentName = agentName;
  } else {
    delete newQueryParams.agentName;
  }
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }

  return {...newQueryParams, ...filter};
};

export function PaypalRequestsFilter() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const paypalRequestsContext = usePaypalRequestsContext();
  const paypalRequestsProps = useMemo(() => {
    return {
      queryParams: paypalRequestsContext.queryParams,
      setQueryParams: paypalRequestsContext.setQueryParams,
    };
  }, [paypalRequestsContext]);
  const [loading, setLoading] = useState(false);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getPaypalRequestsListIDPrefix);

    const newQueryParams = prepareFilter(paypalRequestsProps.queryParams, values);
    if (!_.isEqual(newQueryParams, paypalRequestsProps.queryParams)) {
      paypalRequestsProps.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  let paypalPaymentID = qs.get('paypalPaymentID') || '';
  let paymentStatus = qs.get('paymentStatus') || '';
  let agentName = qs.get('agentName') || '';
  let agentID = qs.get('agentID') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';

  const handleExport = () => {
    setLoading(true);
    dispatch(actions.getPaypalRequestsListForExcelFile(paypalRequestsProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => generateExcelFile(data, 'Paypal_requests.xlsx', paypalRequestsHeader))
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        agent,
        customer,
        payment,
        createdAt,
        invoiceID,
        paypalPaymentID,
        paymentStatus
      } = item;

      const agentID = _.get(agent, 'agentId', '');
      const agentFirstName = _.get(agent, 'firstName', '');
      const agentLastName = _.get(agent, 'lastName', '');
      const customerName = _.get(customer, 'name', '');
      const customerEmail = _.get(customer, 'email', '');
      const amount = _.get(payment, 'amount', '');
      const currencyCode = _.get(payment, 'currency', '');
      const description = _.get(payment, 'description', '');

      const getStatus = () => {
        switch (paymentStatus) {
          case 'pending':
            return 'בתהליך';
          case 'success':
            return 'שולם';
          case 'cancel':
            return 'בוטל';
          default:
            return '';
        }
      };

      return {
        [headersPaypalRequests[0]]: index + 1,
        [headersPaypalRequests[1]]: agentID,
        [headersPaypalRequests[2]]: [agentFirstName, agentLastName].filter(Boolean).join(' '),
        [headersPaypalRequests[3]]: customerName,
        [headersPaypalRequests[4]]: customerEmail,
        [headersPaypalRequests[5]]: [amount, getCurrencyValue(currencyCode)].filter(Boolean).join(' '),
        [headersPaypalRequests[6]]: description,
        [headersPaypalRequests[7]]: Boolean(createdAt) ? moment(createdAt).format('DD.MM.YYYY') : '',
        [headersPaypalRequests[8]]: invoiceID ? invoiceID : '',
        [headersPaypalRequests[9]]: paypalPaymentID ? paypalPaymentID : '',
        [headersPaypalRequests[10]]: getStatus(),
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <>
      <Formik
        initialValues={{
          paypalPaymentID,
          paymentStatus,
          fromDate,
          toDate,
          agentName,
          agentID
        }}
        onSubmit={(values) => applyFilter(values)}
        enableReinitialize={true}
      >
        {({values, ...formikProps}) => (
          <FiltersBar
            filters={
              <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
                <FilterTextField
                  fieldName={'agentID'}
                  placeholder={intl.formatMessage({id: 'paypal-requests-filter-agentID', defaultMessage: 'Agent ID'})}
                  value={values.agentID}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterTextField
                  fieldName={'agentName'}
                  placeholder={intl.formatMessage({
                    id: 'paypal-requests-filter-agentName',
                    defaultMessage: 'Agent name'
                  })}
                  value={values.agentName}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterSelect
                  fieldName={'paymentStatus'}
                  valueName={values.paymentStatus}
                  options={paypalPaymentStatusOptions}
                  placeholder={intl.formatMessage({
                    id: 'paypal-requests-filter-paymentStatus',
                    defaultMessage: 'Status'
                  })}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterFromDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterToDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterTextField
                  fieldName={'paypalPaymentID'}
                  placeholder={intl.formatMessage({
                    id: 'paypal-requests-filter-paypalPaymentID',
                    defaultMessage: 'Paypal payment ID'
                  })}
                  value={values.paypalPaymentID}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
              </div>
            }
            buttons={
              <ButtonExportToExcel
                loading={loading}
                exportFunc={handleExport}
              />
            }
          />
        )}
      </Formik>
    </>
  );
}
